import { LineParser } from './LineParser';
import { CodeBlockNode } from './types';

/**
 * Builds a tree structure from lines of code
 */
export class TreeBuilder {
  private root: CodeBlockNode[] = [];
  private stack: CodeBlockNode[] = [];
  private currentParent: CodeBlockNode[] = this.root;
  private lineParser: LineParser;
  private colorDefinitions: Map<string, string>;

  constructor(
    private lines: string[],
    defaultColors: Map<string, string> = new Map()
  ) {
    // Start with default colors from the platform
    this.colorDefinitions = new Map(defaultColors);
    this.lineParser = new LineParser(this.colorDefinitions);
  }

  build(): CodeBlockNode[] {
    let i = 0;
    while (i < this.lines.length) {
      const line = this.lines[i];
      const trimmed = line.trim();

      if (!trimmed) {
        i++;
        continue;
      }
      if (this.isStartOfCodeBlock(trimmed)) {
        this.lineParser = new LineParser(this.colorDefinitions);
        i++;
        continue;
      }
      if (this.isEndOfCodeBlock(trimmed)) {
        i++;
        continue;
      }
      if (this.isColorDefinition(trimmed)) {
        this.parseColorDefinition(trimmed);
        i++;
        continue;
      }

      const node = this.lineParser.parseLine(line, 0);
      if (!node) {
        i++;
        continue;
      }

      if (this.isBranchingNode(node) || this.isEndNode(node)) {
        this.popStack();
        if (this.isEndNode(node)) {
          i++;
          continue;
        }
      }

      // Attach content and get the number of lines consumed
      const linesConsumed = this.attachContent(node, i);
      this.currentParent.push(node);

      if (this.isContainerNode(node)) {
        this.pushToStack(node);
      }

      // Skip the lines that were consumed as content
      i += linesConsumed + 1;
    }

    return this.root;
  }

  getColorDefinitions(): Map<string, string> {
    return this.colorDefinitions;
  }

  private isStartOfCodeBlock(trimmed: string): boolean {
    return trimmed.startsWith('@code');
  }

  private isEndOfCodeBlock(trimmed: string): boolean {
    return trimmed === '@code-end';
  }

  private isColorDefinition(trimmed: string): boolean {
    return trimmed.startsWith('@color[');
  }

  private parseColorDefinition(trimmed: string): void {
    const match = trimmed.match(/@color\[key="([^"]+)";value="([^"]+)"\]/);
    if (match) {
      const [, key, value] = match;
      this.colorDefinitions.set(key, value);
    }
  }

  private isEndNode(node: CodeBlockNode): boolean {
    return node.type === 'control' && node.controlType === 'end';
  }

  private isBranchingNode(node: CodeBlockNode): boolean {
    return node.type === 'control' && (node.controlType === 'elseif' || node.controlType === 'else');
  }

  private isContainerNode(node: CodeBlockNode): boolean {
    return node.type === 'control' && node.controlType !== 'end';
  }

  private popStack(): void {
    if (this.stack.length > 0) {
      this.stack.pop();
      this.currentParent = this.stack.length > 0 ? this.stack[this.stack.length - 1].children! : this.root;
    }
  }

  private pushToStack(node: CodeBlockNode): void {
    this.stack.push(node);
    this.currentParent = node.children!;
  }

  private attachContent(node: CodeBlockNode, currentIndex: number): number {
    let linesConsumed = 0;

    if (currentIndex + 1 >= this.lines.length) return linesConsumed;

    const nextLine = this.lines[currentIndex + 1].trim();
    if (!nextLine || nextLine.startsWith('@')) return linesConsumed;

    if (node.type === 'code' && !node.content) {
      node.content = nextLine;
      linesConsumed = 1;
    } else if (node.type === 'control' && !node.condition) {
      node.condition = nextLine;
      linesConsumed = 1;
    }

    return linesConsumed;
  }
}
